import { Dialog, Transition } from '@headlessui/react';
import { MailIcon, XIcon } from '@heroicons/react/outline';
import Image from 'next/image';
import Link from 'next/link';
import Trans from 'next-translate/Trans';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from 'react-use';

import clsxm from '@/lib/clsxm';
import { localeDirection } from '@/lib/textDirection';
import { useLastUsedLoginMethod } from '@/hooks/useLastUsedLoginMethod';

import { COMPLIANCE, TermsNote } from '@/components/auth/AuthLayout';
import {
  type AuthErrorReason,
  describeAuthMessage,
  EXISTING_ACCOUNT_MODAL,
} from '@/components/auth/authMessages';
import AuthTurnstile from '@/components/auth/AuthTurnstile';
import {
  buildUniqueEmailHistory,
  EMAIL_HISTORY_LIMIT,
  inboxProviderFor,
  normalizeEmail,
} from '@/components/auth/emailHistory';
import {
  AuthButton,
  AuthHeading,
  buttonClass,
  fieldClass,
  fieldInvalid,
  focusRing,
  labelClass,
  linkClass,
  Notice,
  OrDivider,
  SentConfirmation,
  ShieldGlyph,
  StatusIcon,
  tone,
  useAuthT,
  useAuthTrack,
} from '@/components/auth/kit';
import OAuthButtons from '@/components/auth/OAuthButtons';
import PasswordField from '@/components/auth/PasswordField';
import LastUsedBadge from '@/components/LastUsedBadge';
import {
  isTurnstileEnabled,
  TURNSTILE_ERROR_MESSAGE,
  type TurnstileHandle,
} from '@/components/Turnstile';

import { useAnalytics } from '@/contexts/Analytics';
import { validatePassword } from '@/utils/passwordPolicy';
import { createClient } from '@/utils/supabase/component';

interface AuthModalProps {
  showModal: boolean;
  onClose: () => void;
  afterClose?: () => void;
  redirectTo: string;
  botName?: string;
  botAvatar?: string;
  /** Which form shows first. Sign in unless the opener said "Sign up". */
  defaultMode?: 'sign_in' | 'sign_up';
}

export default function AuthModal({
  showModal,
  onClose,
  afterClose,
  redirectTo,
  botName,
  botAvatar,
  defaultMode = 'sign_in',
}: AuthModalProps) {
  const supabase = createClient();
  const analytics = useAnalytics();
  const { t, lang } = useAuthT();
  const track = useAuthTrack('modal');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: 'error' | 'success';
    text: string;
  } | null>(null);
  const [isSignUp, setIsSignUp] = useState(defaultMode === 'sign_up');

  const [storedEmailHistory, setStoredEmailHistory] = useLocalStorage<string[]>(
    'emailHistory',
    [],
  );
  const { lastUsedLoginMethod, setLastUsedLoginMethod } =
    useLastUsedLoginMethod();

  const uniqueEmailHistory = buildUniqueEmailHistory(storedEmailHistory);
  const normalizedEmailValue = normalizeEmail(email);
  const hasReachedEmailLimit = uniqueEmailHistory.length >= EMAIL_HISTORY_LIMIT;
  const emailAlreadyTracked = normalizedEmailValue
    ? uniqueEmailHistory.some(
        (entry) => normalizeEmail(entry) === normalizedEmailValue,
      )
    : false;
  const isBlockedByEmailLimit =
    isSignUp &&
    normalizedEmailValue.length > 0 &&
    hasReachedEmailLimit &&
    !emailAlreadyTracked;
  const passwordValidation = validatePassword(password, email);
  // Supabase Auth rejects sign-in/sign-up without a captcha token once Attack
  // Protection is on.
  const captchaRef = useRef<TurnstileHandle>(null);
  const submitButtonDisabled =
    loading ||
    (isSignUp && isBlockedByEmailLimit) ||
    (isSignUp && password.length > 0 && !passwordValidation.isValid);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const captchaToken = await captchaRef.current?.getToken();
      if (isTurnstileEnabled && !captchaToken) {
        setMessage({ type: 'error', text: TURNSTILE_ERROR_MESSAGE });
        return;
      }

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      });

      if (error) {
        setMessage({ type: 'error', text: error.message });
      } else {
        setLastUsedLoginMethod('email');
        window.location.href = redirectTo;
      }
    } catch {
      setMessage({ type: 'error', text: 'An unexpected error occurred' });
    } finally {
      // Turnstile tokens are single use — clear it whatever the outcome.
      captchaRef.current?.reset();
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (normalizedEmailValue && hasReachedEmailLimit && !emailAlreadyTracked) {
      setMessage({
        type: 'error',
        text: `You've reached the limit of ${EMAIL_HISTORY_LIMIT} accounts on this device.`,
      });
      return;
    }

    if (!passwordValidation.isValid) {
      setMessage({
        type: 'error',
        text:
          passwordValidation.firstError ??
          'Password does not meet the security requirements.',
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch('/api/validate-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const validation = await response.json();
      if (!validation.isValid) {
        setMessage({
          type: 'error',
          text: validation.reason || 'Invalid email address',
        });
        setLoading(false);
        return;
      }

      const captchaToken = await captchaRef.current?.getToken();
      if (isTurnstileEnabled && !captchaToken) {
        setMessage({ type: 'error', text: TURNSTILE_ERROR_MESSAGE });
        return;
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/confirm?next=${encodeURIComponent(redirectTo)}`,
          captchaToken,
        },
      });

      if (error) {
        setMessage({ type: 'error', text: error.message });
      } else if ((data.user?.identities?.length ?? 0) === 0) {
        // Existing CONFIRMED account (e.g. created via Google): Supabase
        // anti-enumeration returns a fake "success" with an identity-less
        // user and sends NO email, so "check your email" would strand the
        // user. See login.tsx for the full story; same handling here.
        analytics.track('Sign Up Existing Account', {
          method: 'email',
          surface: 'modal',
        });
        // Fresh captcha token for the courtesy email BEFORE flipping the
        // view — the flip re-renders the widget with the 'login' action and
        // would void the pending token request.
        captchaRef.current?.reset();
        const noticeToken = await captchaRef.current?.getToken();
        setIsSignUp(false);
        setMessage({
          type: 'error',
          text: EXISTING_ACCOUNT_MODAL,
        });
        // Best-effort: emails the mailbox owner which provider they signed
        // up with plus a one-click sign-in link. Fire-and-forget by design.
        fetch('/api/notify-existing-account', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, captchaToken: noticeToken }),
        }).catch(() => undefined);
      } else {
        // See login.tsx: browser-side twin of the server-fired
        // `Sign Up Success`, carrying the session/referrer/UTMs the webhook
        // event cannot.
        analytics.track('Sign Up Started', {
          method: 'email',
          surface: 'modal',
        });
        // Lets the first authenticated load fire `Sign Up Completed` even if
        // the confirmation email is opened hours later (useUser.tsx).
        try {
          localStorage.setItem('ep_pending_signup', String(Date.now()));
        } catch {
          // storage unavailable — first-session detection still covers it.
        }

        setStoredEmailHistory((prev) => {
          const existing = buildUniqueEmailHistory(prev);
          if (
            normalizedEmailValue &&
            !existing.some((e) => normalizeEmail(e) === normalizedEmailValue)
          ) {
            existing.push(email.trim());
          }
          return existing.slice(0, EMAIL_HISTORY_LIMIT);
        });
        setMessage({
          type: 'success',
          text: 'Check your email for the confirmation link',
        });
      }
    } catch {
      setMessage({ type: 'error', text: 'An unexpected error occurred' });
    } finally {
      // Turnstile tokens are single use — clear it whatever the outcome.
      captchaRef.current?.reset();
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider: 'google' | 'facebook') => {
    setLoading(true);
    // Remember the chosen provider before redirecting to the OAuth flow so the
    // "Last used" hint is ready the next time the user opens this modal.
    setLastUsedLoginMethod(provider);
    try {
      const callbackUrl = `${window.location.origin}/auth/callback?next=${encodeURIComponent(redirectTo)}`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: callbackUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        setMessage({ type: 'error', text: error.message });
      }
    } catch {
      setMessage({ type: 'error', text: 'An unexpected error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const mode = isSignUp ? 'sign_up' : 'sign_in';
  const described =
    message?.type === 'error' ? describeAuthMessage(message.text) : null;
  // After an email sign-up the form gives way to "check your inbox".
  const sentTo = message?.type === 'success' ? email.trim() : null;
  const inbox = inboxProviderFor(sentTo);

  // How many visitors the modal reaches, where (autocapture is off).
  useEffect(() => {
    if (!showModal) return;
    analytics.track('Auth Modal Opened', {
      path: window.location.pathname,
      mode: isSignUp ? 'sign_up' : 'sign_in',
      bot: Boolean(botName),
    });
    // Counted once per opening, in the mode it opened in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  useEffect(() => {
    if (message?.type !== 'error') return;
    track.error({ mode, reason: describeAuthMessage(message.text).reason });
    // `mode` is read when the error appears; a later mode switch is no event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, track]);

  const switchMode = (toSignUp: boolean) => {
    setIsSignUp(toSignUp);
    setMessage(null);
    track.modeSwitched(toSignUp ? 'sign_up' : 'sign_in');
  };

  const onOAuth = (provider: 'google' | 'facebook') => {
    track.started({ mode, method: provider });
    handleOAuthSignIn(provider);
  };

  const onSubmit = (event: React.FormEvent) => {
    track.started({ mode, method: 'email' });
    return isSignUp ? handleSignUp(event) : handleSignIn(event);
  };

  const errorAction = (reason: AuthErrorReason) => {
    if (reason === 'invalid_credentials' && !isSignUp) {
      return (
        <button
          type='button'
          onClick={() => switchMode(true)}
          className={clsxm(linkClass, 'text-[14px]')}
        >
          {t('New here? Create an account')}
        </button>
      );
    }
    if (reason === 'gmail_inbox_taken' && isSignUp) {
      return (
        <button
          type='button'
          onClick={() => switchMode(false)}
          className={clsxm(linkClass, 'text-[14px]')}
        >
          {t('Sign in instead')}
        </button>
      );
    }
    if (reason === 'existing_account') {
      return (
        <Link
          href='/auth/forgot-password'
          className={clsxm(linkClass, 'text-[14px]')}
        >
          {t('Reset your password')}
        </Link>
      );
    }
    return null;
  };

  const switchLine = isSignUp ? (
    <Trans
      i18nKey='Already have an account? <0>Sign in</0>'
      ns='login'
      defaultTrans='Already have an account? <0>Sign in</0>'
      components={[
        <button
          key='switch'
          type='button'
          onClick={() => switchMode(false)}
          className={linkClass}
        />,
      ]}
    />
  ) : (
    <Trans
      i18nKey='New to Easy-Peasy.AI? <0>Create a free account</0>'
      ns='login'
      defaultTrans='New to Easy-Peasy.AI? <0>Create a free account</0>'
      components={[
        <button
          key='switch'
          type='button'
          onClick={() => switchMode(true)}
          className={linkClass}
        />,
      ]}
    />
  );

  return (
    <Transition.Root show={showModal} as={Fragment} afterLeave={afterClose}>
      <Dialog as='div' className='relative z-[999]' onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-[#0d0c1f]/55 backdrop-blur-sm transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-50 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-3 sm:p-6'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-3 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 sm:scale-100'
              leaveTo='opacity-0 sm:scale-95'
            >
              <Dialog.Panel
                dir={localeDirection(lang)}
                className={clsxm(
                  'relative w-full max-w-[440px] transform overflow-hidden rounded-[28px] bg-white text-start shadow-[0_40px_100px_-30px_rgba(22,12,90,0.55)] ring-1 ring-[#0f0f28]/[0.08] transition-all dark:bg-[#14141f] dark:ring-white/10 [&:lang(ko)]:break-keep',
                )}
              >
                <button
                  type='button'
                  className={clsxm(
                    'absolute end-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-[#0f0f28]/[0.06] hover:text-[#10101c] dark:hover:bg-white/10 dark:hover:text-white',
                    tone.quiet,
                    focusRing,
                  )}
                  onClick={onClose}
                  aria-label={t('Close')}
                >
                  <XIcon className='h-5 w-5' aria-hidden='true' />
                </button>

                <div className='px-6 pb-7 pt-8 sm:px-9 sm:pb-9 sm:pt-10'>
                  {sentTo ? (
                    <div>
                      <AuthHeading
                        size='md'
                        icon={
                          <StatusIcon>
                            <MailIcon />
                          </StatusIcon>
                        }
                        title={
                          <Dialog.Title as='span'>
                            {t('Check your inbox')}
                          </Dialog.Title>
                        }
                      >
                        <p>
                          <SentConfirmation email={sentTo} />
                        </p>
                      </AuthHeading>
                      <div className='mt-7 space-y-3'>
                        {inbox && (
                          <a
                            href={inbox.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={buttonClass('primary')}
                          >
                            {t('Open {{provider}}', { provider: inbox.name })}
                          </a>
                        )}
                        <AuthButton
                          variant='secondary'
                          onClick={() => setMessage(null)}
                        >
                          {t('Use a different email')}
                        </AuthButton>
                      </div>
                      <p
                        className={clsxm(
                          'mt-5 text-[13px] leading-5',
                          tone.quiet,
                        )}
                      >
                        {t("Didn't get it? Check your spam folder.")}
                      </p>
                    </div>
                  ) : (
                    <>
                      {botName ? (
                        <div className='flex flex-col items-center text-center'>
                          {botAvatar ? (
                            <Image
                              src={botAvatar}
                              width={56}
                              height={56}
                              alt=''
                              className='h-14 w-14 rounded-2xl object-cover ring-1 ring-[#0f0f28]/10 dark:ring-white/10'
                            />
                          ) : (
                            <div
                              aria-hidden='true'
                              className='flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-400 to-primary-600 text-xl font-semibold text-white'
                            >
                              {botName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <Dialog.Title
                            className={clsxm(
                              'mt-4 text-[1.375rem] font-semibold leading-tight tracking-[-0.01em]',
                              tone.ink,
                            )}
                          >
                            {isSignUp
                              ? t('Sign up to chat with {{name}}', {
                                  name: botName,
                                })
                              : t('Sign in to chat with {{name}}', {
                                  name: botName,
                                })}
                          </Dialog.Title>
                          <p
                            className={clsxm(
                              'mt-2 text-[15px] leading-6',
                              tone.body,
                            )}
                          >
                            {switchLine}
                          </p>
                        </div>
                      ) : (
                        <AuthHeading
                          size='md'
                          icon={
                            <Image
                              src='/apple-touch-icon.png'
                              width={48}
                              height={48}
                              alt='Easy-Peasy.AI'
                              className='h-12 w-12 rounded-2xl ring-1 ring-[#0f0f28]/[0.08] dark:ring-white/10'
                            />
                          }
                          title={
                            <Dialog.Title as='span'>
                              {isSignUp
                                ? t('Create your free account')
                                : t('Welcome back')}
                            </Dialog.Title>
                          }
                        >
                          <p>{switchLine}</p>
                        </AuthHeading>
                      )}

                      <div className='mt-7'>
                        <OAuthButtons
                          onSelect={onOAuth}
                          disabled={loading}
                          lastUsed={lastUsedLoginMethod}
                          errorKey={message}
                        />
                      </div>

                      <div className='my-6'>
                        <OrDivider label={t('or')} />
                      </div>

                      <form onSubmit={onSubmit}>
                        <div className='space-y-5'>
                          <div>
                            <label
                              htmlFor='auth-modal-email'
                              className={labelClass}
                            >
                              {t('Email address')}
                            </label>
                            <input
                              id='auth-modal-email'
                              name='email'
                              type='email'
                              inputMode='email'
                              autoComplete={isSignUp ? 'email' : 'username'}
                              autoCapitalize='none'
                              autoCorrect='off'
                              spellCheck={false}
                              required
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              aria-invalid={isBlockedByEmailLimit || undefined}
                              aria-describedby={
                                isBlockedByEmailLimit
                                  ? 'auth-modal-email-limit'
                                  : undefined
                              }
                              className={clsxm(
                                fieldClass,
                                'mt-2',
                                isBlockedByEmailLimit && fieldInvalid,
                              )}
                              placeholder='name@example.com'
                            />
                            {isBlockedByEmailLimit && (
                              <p
                                id='auth-modal-email-limit'
                                className='mt-2 text-[13px] leading-5 text-red-700 dark:text-red-300'
                              >
                                {t(
                                  "You've reached the limit of {{limit}} accounts on this device. Please continue with an existing email or contact support for assistance.",
                                  { limit: EMAIL_HISTORY_LIMIT },
                                )}
                              </p>
                            )}
                          </div>

                          <PasswordField
                            id='auth-modal-password'
                            label={t('Password')}
                            value={password}
                            onChange={setPassword}
                            autoComplete={
                              isSignUp ? 'new-password' : 'current-password'
                            }
                            minLength={isSignUp ? 12 : undefined}
                            rules={
                              isSignUp ? passwordValidation.rules : undefined
                            }
                            invalid={
                              isSignUp && password.length > 0
                                ? !passwordValidation.isValid
                                : undefined
                            }
                            labelAside={
                              !isSignUp && (
                                <Link
                                  href='/auth/forgot-password'
                                  className={clsxm(linkClass, 'text-[13px]')}
                                >
                                  {t('Forgot password?')}
                                </Link>
                              )
                            }
                          />
                        </div>

                        <div className='mt-6'>
                          <AuthTurnstile
                            ref={captchaRef}
                            action={isSignUp ? 'signup' : 'login'}
                            className='mb-4'
                          />

                          {described &&
                            !(
                              described.reason === 'device_limit' &&
                              isBlockedByEmailLimit
                            ) && (
                              <Notice
                                kind='error'
                                className='mb-4'
                                action={errorAction(described.reason)}
                              >
                                {t(described.key, described.values)}
                              </Notice>
                            )}

                          <AuthButton
                            type='submit'
                            loading={loading}
                            disabled={submitButtonDisabled}
                          >
                            {loading
                              ? isSignUp
                                ? t('Creating your account...')
                                : t('Signing in...')
                              : isSignUp
                                ? t('Create account')
                                : t('Sign in')}
                            {!isSignUp &&
                              lastUsedLoginMethod === 'email' &&
                              !loading && (
                                <LastUsedBadge
                                  label={t('Last used')}
                                  variant='onPrimary'
                                />
                              )}
                          </AuthButton>
                        </div>
                      </form>
                    </>
                  )}

                  <TermsNote className='mt-7' />
                  <p
                    className={clsxm(
                      'mt-4 flex items-center justify-center gap-1.5 text-[12px]',
                      tone.quiet,
                    )}
                  >
                    <ShieldGlyph />
                    <span dir='ltr'>{COMPLIANCE.join(' · ')}</span>
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
